/**
 * PostHog initialization — fail-safe, isolated, consent-gated.
 *
 * Three gates must all pass before PostHog captures anything:
 *   1. `REACT_APP_POSTHOG_KEY` is set at build time (integration active).
 *   2. The user granted `analytics` consent via the cookie banner.
 *   3. We haven't already initialized.
 *
 * When all three pass, we start a *named* instance ("qortrace") so the
 * app's analytics stay isolated from Emergent's platform posthog snippet
 * injected in public/index.html.
 *
 * Session replay is pre-configured with privacy-hardened defaults that
 * mask all inputs + any element with `[data-ph-mask]` or `.ph-mask`,
 * plus wallet-address containers. Network bodies are never recorded.
 *
 * Configure via:
 *   REACT_APP_POSTHOG_KEY  — project API key (phc_...)
 *   REACT_APP_POSTHOG_HOST — defaults to https://us.i.posthog.com,
 *                            set to https://eu.i.posthog.com for EU.
 */
import posthog from "posthog-js";
import { readConsentSync } from "./consent";

const KEY = process.env.REACT_APP_POSTHOG_KEY;
const HOST = process.env.REACT_APP_POSTHOG_HOST || "https://us.i.posthog.com";
const INSTANCE_NAME = "qortrace";

export const isPostHogEnabled = Boolean(KEY);

let initialized = false;
let instance = null;

function hasAnalyticsConsent() {
  const stored = readConsentSync();
  return Boolean(stored && stored.preferences && stored.preferences.analytics);
}

function doInit() {
  try {
    instance = posthog.init(
      KEY,
      {
        api_host: HOST,
        capture_pageview: false,
        autocapture: { capture_form_inputs: false },
        persistence: "localStorage+cookie",
        disable_session_recording: false,
        session_recording: {
          maskAllInputs: true,
          maskTextSelector:
            "[data-ph-mask], .ph-mask, [data-wallet-address], code.address",
          recordCrossOriginIframes: false,
          recordHeaders: false,
          recordBody: false,
        },
      },
      INSTANCE_NAME
    );
    initialized = true;
    // Expose on window for diagnostics + so call-sites that want to
    // sanity-check the integration can do `window.__qtPosthog`. The
    // Emergent preview platform owns `window.posthog` for its own
    // telemetry, so we deliberately use a different global name.
    if (typeof window !== "undefined") {
      window.__qtPosthog = instance;
    }
    // eslint-disable-next-line no-console
    console.info("[posthog] initialized:", INSTANCE_NAME, "→", HOST);
    return true;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error("[posthog] init failed:", err);
    return false;
  }
}

export function initPostHog() {
  if (!isPostHogEnabled || initialized) return false;
  if (!hasAnalyticsConsent()) {
    // Listen for a later consent grant and re-attempt exactly once.
    if (typeof window !== "undefined") {
      const handler = (e) => {
        if (e && e.detail && e.detail.preferences && e.detail.preferences.analytics) {
          window.removeEventListener("qt:consent-change", handler);
          if (!initialized && isPostHogEnabled) doInit();
        } else if (e && e.detail && e.detail.preferences && !e.detail.preferences.analytics) {
          // User rejected — nothing to do; we simply never initialized.
        }
      };
      window.addEventListener("qt:consent-change", handler);
    }
    return false;
  }
  return doInit();
}

export function getInstance() {
  if (!isPostHogEnabled) return null;
  return instance;
}
